import axios from "axios";

import {
  DEFAULT_ZIP_CODES_BASE_URL,
  resolveZipCodesBaseUrl,
} from "../api/zip-codes";
import { normalizeUrlForFingerprint } from "./apiErrorReporting";
import { readExpectedBusinessResponseMark } from "./expectedBusinessResponseMark";

/**
 * Tabla DECLARATIVA de respuestas de negocio ESPERADAS.
 *
 * `shouldIgnoreApiError` responde a una pregunta distinta ("el cliente se
 * rindio o el transporte fallo") y por diseño JAMAS descarta un error que trae
 * una respuesta HTTP. Este modulo responde a la otra: "el backend contesto
 * exactamente lo que este flujo espera". Son dos decisiones separadas a
 * proposito; mezclarlas rompia el invariante instrumentado en
 * `apiErrorReporting.test.ts` ("never drops an error that carries an HTTP
 * response").
 *
 * Nace de cuatro issues de Sentry que entre los cuatro sumaban ~3540 eventos de
 * flujos que funcionan (MB-APP-EXPRESS-EV ~1284, -D 1383, -19K, -19J) y absorbe
 * el bloque hardcodeado del NIP incorrecto, que vivia en UNA sola de las tres
 * puertas a Sentry y estaba atado al prefijo literal `${API}`.
 *
 * TRES INVARIANTES DE DISEÑO, y el resto del modulo existe para sostenerlos.
 * Los tres estan INSTRUMENTADOS en `expectedBusinessResponses.test.ts`
 * ("higiene de la tabla"), que recorre la tabla real; y ese test tiene a su vez
 * un test de no-placebo que le inyecta, una por una, cada forma de regla
 * insegura que se intento colar, y exige que las rechace. Si añades una regla,
 * el que tiene que pasar es el de higiene: no hay que acordarse de nada.
 *
 * 1. FALLAR ABIERTO. Ante cualquier duda —un cuerpo con otra forma, una url
 *    que no normaliza igual, una marca que se perdio, un `matches` que lanza—
 *    el error SE CAPTURA. Una variante que no casa sigue siendo visible en
 *    Sentry; eso es ruido, no un bug invisible. Nunca al contrario.
 *
 * 2. NUNCA SOLO POR STATUS Y RUTA. Toda regla exige metodo + ruta ANCLADA +
 *    status exacto + un discriminador del CUERPO, porque los mismos
 *    status+ruta tienen variante esperada y variante bug: un 400 de
 *    editor-data es "ya tiene contrato" o un payload malformado; un 404 de
 *    investigations es "no existe" o un fallo de scoping; un 400 del
 *    multireporte publico es "el token caduco" o "Access denied.".
 *
 *    Que el discriminador del cuerpo sea REAL no se pide por escrito: cada
 *    regla declara su `probe` (la peticion y el cuerpo de verdad que reconoce,
 *    copiados de Sentry) y la higiene la obliga a devolver `true` con ese
 *    cuerpo y `false` con ocho cuerpos ajenos. Una regla que no mire el cuerpo
 *    no puede pasar las dos cosas a la vez.
 *
 * 3. CUANDO EL ERROR NO DISTINGUE, DECIDE LA PROCEDENCIA. Si el MISMO endpoint
 *    devuelve el MISMO cuerpo en un flujo sano y en un bug —el caso de R1—, el
 *    error ya no contiene la respuesta, y entonces la regla exige que el call
 *    site haya DECLARADO que espera esa respuesta
 *    (`expectedBusinessResponseMark.ts`). Es una afirmacion de quien se hace
 *    responsable de manejarla, viaja con la peticion y no depende de donde
 *    este el navegador cuando llega la respuesta. Callar 1283 eventos no vale
 *    un bug invisible, y amarrar la regla a una ruta de la SPA tampoco: se
 *    probo y perdia el bucket mas grande del issue (ver R1).
 *
 * Deliberadamente NO se casa por el texto del mensaje en español: si el
 * backend enciende i18n, la regla dejaria de casar (por el lado seguro, pero
 * el ruido volveria). Se casa por la forma del dato. La unica excepcion es R5,
 * y alli se explica por que y en que direccion falla.
 */

/** Los facts que ve una regla. */
export interface ExpectedBusinessResponseFacts {
  /** `error.response.data` tal cual. Puede ser objeto, string, Blob, null... */
  body: unknown;
  /**
   * Query efectiva de la peticion: la inline del url MAS `config.params`.
   *
   * Las dos fuentes son obligatorias. Axios NO escribe `params` en
   * `config.url` (verificado: `config.url` queda
   * `/v1/tenants/reports/313077/contract/editor-data` y `config.params` queda
   * `{force:"true"}`), asi que una regla que buscara `?force=true` en el url
   * nunca lo veria.
   */
  query: Record<string, string>;
  /**
   * La marca que el CALL SITE puso en la config de axios para declarar que
   * espera esta respuesta, o `""` si no hay ninguna.
   *
   * Es el unico hecho que no sale del error: sale de quien lanzo la peticion.
   * Solo lo usa la regla cuyo flujo sano y cuyo bug son indistinguibles en la
   * peticion y en la respuesta (R1). Ver `expectedBusinessResponseMark.ts`.
   */
  callSiteMark: string;
}

/**
 * La prueba viva de una regla: una peticion y un cuerpo REALES que tiene que
 * reconocer, copiados del evento de Sentry que la justifica.
 *
 * No es documentacion: es lo que vuelve ejecutable el invariante 2. El test de
 * higiene normaliza `requestPath` con el MISMO normalizador que usa el modulo y
 * exige que `path` case con el resultado y NO case con el resultado mas un
 * segmento (lo que rechaza un `.*` disfrazado de ruta anclada); y llama a
 * `matches` con estos facts para exigir que diga `true` con `body` y `false`
 * con cuerpos ajenos (lo que rechaza una regla que no mire el cuerpo).
 */
export interface ExpectedBusinessResponseProbe {
  /** Ruta real de la peticion, SIN query, tal cual la escribe el call site. */
  requestPath: string;
  /** Query efectiva del caso esperado. */
  query: Record<string, string>;
  /** Marca que la regla exige, o `""` si no exige ninguna. */
  callSiteMark: string;
  /** Cuerpo real que la regla reconoce. */
  body: unknown;
}

export interface ExpectedBusinessResponse {
  /** Id estable. Viaja al breadcrumb como `data.reason`. */
  id: string;
  /** Metodo exacto, ya en mayusculas. */
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Status exactos, nunca rangos: un 500 del mismo endpoint es otro asunto. */
  statuses: readonly number[];
  /**
   * Ruta ANCLADA (`^...$`) y SIN comodines, contra
   * `normalizeUrlForFingerprint(url sin query)`.
   *
   * Reusar el normalizador de la huella es la decision central: resuelve gratis
   * absoluto-vs-relativo, el prefijo `/api`, los ids (uuid / numerico / opaco)
   * y —crucial— CONSERVA el host cuando es ajeno, asi que
   * `external.example/v2/reports/:id/confirm-pin` nunca casa con la regla de
   * `/v2/reports/:id/confirm-pin`. Una sola forma canonica en todo el modulo
   * garantiza que la regla y la huella no se desincronicen.
   *
   * "Anclada" no basta por si sola —`/^\/v1\/tenants\/properties\/.*$/` empieza
   * por `^` y acaba en `$` y sin embargo ciega un arbol entero—, asi que la
   * higiene lo comprueba por COMPORTAMIENTO con `probe.requestPath` y no
   * leyendo el texto del regex. Y sin flags: un `/g` compartido avanza
   * `lastIndex` entre llamadas y volveria el filtro no determinista.
   */
  path: RegExp;
  /** Discriminador de contenido. Obligatorio: ver invariante 2. */
  matches: (facts: ExpectedBusinessResponseFacts) => boolean;
  /** La peticion y el cuerpo reales que la regla reconoce. Ver invariante 2. */
  probe: ExpectedBusinessResponseProbe;
  /** Justificacion y issue de Sentry. Lo lee el humano que audite la tabla. */
  why: string;
}

/**
 * Lectura defensiva de un campo del cuerpo.
 *
 * `response.data` no siempre es un objeto: las descargas usan
 * `responseType: "blob"` y un 404 de proxy llega como string HTML. Sobre un
 * Blob, un array o un string esto devuelve `undefined` sin lanzar, y que el
 * cuerpo no tenga la forma esperada es justamente lo que hace que esas
 * respuestas NO casen (que es lo correcto).
 */
const bodyField = (body: unknown, key: string): unknown => {
  if (typeof body !== "object" || body === null) {
    return undefined;
  }
  return (body as Record<string, unknown>)[key];
};

const escapeForRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const ZIP_CODE_NORMALIZED_SUFFIX = "/zip-code/:id";

/**
 * Como normaliza `normalizeUrlForFingerprint` las urls del servicio de codigos
 * postales: el prefijo de host con el que quedan, y una url de muestra real
 * para el `probe` de la regla.
 *
 * Las dos cosas se DERIVAN normalizando la misma base URL que usa
 * `api/zip-codes.ts` en vez de escribir el host a mano: asi el prefijo, la
 * huella y la prueba salen del mismo codigo, y si el deploy apunta el servicio
 * a otro host (`VITE_ZIP_CODES_API_URL`) los tres lo siguen.
 *
 * Todo dentro de try/catch con el host literal por defecto: este modulo entra
 * en la cadena de arranque de `sentry.ts` y un throw al evaluarlo tumbaria la
 * app entera. `resolveZipCodesBaseUrl` ya trae su propio fallback, asi que
 * esto solo cubre un override tan mal escrito que rompa la normalizacion.
 */
const ZIP_CODES_NORMALIZATION = ((): {
  hostPrefix: string;
  sampleRequestPath: string;
} => {
  // `DEFAULT_ZIP_CODES_BASE_URL` es un literal string en `api/zip-codes.ts`,
  // asi que esta linea —la unica fuera del try— no puede lanzar.
  const fallback = {
    hostPrefix: DEFAULT_ZIP_CODES_BASE_URL.replace(
      /^[a-z][a-z0-9+.-]*:\/\//i,
      "",
    ),
    sampleRequestPath: `${DEFAULT_ZIP_CODES_BASE_URL}/zip-code/44600`,
  };

  try {
    const baseUrl = resolveZipCodesBaseUrl(
      import.meta.env.VITE_ZIP_CODES_API_URL,
    );
    const normalized = normalizeUrlForFingerprint(`${baseUrl}/zip-code/00000`);

    if (!normalized.endsWith(ZIP_CODE_NORMALIZED_SUFFIX)) {
      return fallback;
    }

    return {
      hostPrefix: normalized.slice(0, -ZIP_CODE_NORMALIZED_SUFFIX.length),
      sampleRequestPath: `${baseUrl}/zip-code/44600`,
    };
  } catch {
    return fallback;
  }
})();

/**
 * El host va OPCIONAL a proposito. `normalizeUrlForFingerprint` solo conserva
 * el host cuando es distinto al del API principal, asi que en un deploy donde
 * el servicio de CP viviera en el mismo origen el prefijo desapareceria y la
 * regla, anclada al host, dejaria de casar. `/zip-code/:id` a secas no existe
 * en el API principal, asi que aceptar las dos formas no abre nada.
 */
const ZIP_CODE_PATH = new RegExp(
  `^(?:${escapeForRegExp(ZIP_CODES_NORMALIZATION.hostPrefix)})?${escapeForRegExp(
    ZIP_CODE_NORMALIZED_SUFFIX,
  )}$`,
);

export const EXPECTED_BUSINESS_RESPONSES: readonly ExpectedBusinessResponse[] =
  [
    {
      id: "editor-data-folio-already-has-contract",
      method: "GET",
      statuses: [400],
      path: /^\/v1\/tenants\/reports\/:id\/contract\/editor-data$/,
      matches: ({ body, query, callSiteMark }) => {
        // Con `force=true` el backend BYPASEA la guarda de "ya tiene
        // contrato", asi que un 400 con `contract_id` en ESA variante si es un
        // bug (del backend) y tiene que seguir viendose.
        if (query.force !== undefined) {
          return false;
        }

        const contractId = bodyField(body, "contract_id");
        if (typeof contractId !== "string" || contractId === "") {
          return false;
        }

        // Y el discriminador que de verdad separa los dos casos, porque el
        // cuerpo NO basta y la asimetria importa: el bug de esta familia se
        // caracteriza justo por la AUSENCIA de `force` (un call site que
        // deberia mandarlo y no lo manda), asi que rendirse solo cuando `force`
        // SI viaja deja ciego exactamente al caso peligroso.
        //
        //   esperado: `useContractEditorData` (useContractEditorData.ts:86-99)
        //             es el UNICO camino del modo create-from-folio
        //             (ContractEditorPage.tsx:139 solo lo habilita ahi): pide
        //             editor-data del folio sin force, el folio ya tiene
        //             contrato, y la pagina lee `contract_id` del cuerpo para
        //             ofrecer "Editar contrato existente". Esa query —y solo
        //             esa— pasa `expectFolioAlreadyHasContract`, asi que llega
        //             aqui con marca.
        //
        //   bug:      en `/contracts/<contrato>/edit`, "Actualizar datos del
        //             multiexpediente" llama a editor-data sin force para un
        //             contrato standalone (ContractEditorPage.tsx:376-378),
        //             cuando el auto-fetch del MISMO folio en el MISMO estado
        //             si manda force porque la guarda dispara
        //             (useContractEditorData.ts:169-179). El boton no refresca
        //             nunca. Esa llamada NO lleva marca, asi que el 400 se
        //             sigue viendo en Sentry.
        //
        // Por que la marca y no la pantalla, que fue el primer intento: la
        // pantalla se lee cuando LLEGA la respuesta, y en MB-APP-EXPRESS-EV el
        // bucket de `url` mas grande del issue (41 eventos en 90d, frente a 12
        // del siguiente) es el dashboard pelado `/contracts`, no
        // `/contracts/<folio>/create` — la respuesta llega despues de que el
        // usuario navego, o el `retry: 1` global reintenta ya fuera de la
        // pantalla (eventos 9e7f11dc857d43878f57396315a3b549 y
        // 5aded81bef484fd6ad51ec526363bb25, los dos con este cuerpo exacto y
        // sin force). La marca viaja con la peticion: ni la navegacion, ni el
        // reintento, ni un rename de ruta la mueven.
        return callSiteMark === "editor-data-folio-already-has-contract";
      },
      probe: {
        requestPath: "/v1/tenants/reports/313147/contract/editor-data",
        query: {},
        callSiteMark: "editor-data-folio-already-has-contract",
        // Cuerpo literal del evento 9e7f11dc857d43878f57396315a3b549.
        body: {
          contract_id: "b39a7a66-fdd6-4a19-bd69-21f089f7618f",
          document_id: "MBO1184-2026",
          message: "Este folio ya tiene un contrato creado",
        },
      },
      why:
        "MB-APP-EXPRESS-EV (~1284 eventos en 90d). `contract_id` es el MISMO " +
        "dato que el front usa para ofrecer 'Editar contrato existente' " +
        "(ContractEditorPage.tsx, `existingContractIdFromError`): si viene, el " +
        "backend contesto 'ya existe, ve alla'. Un payload malformado es un 400 " +
        "de DRF con claves de campo (`{folder_id:['This field is required.']}`) " +
        "y jamas trae `contract_id`, asi que sigue capturandose. Pero el cuerpo " +
        "NO basta: el mismo 400 sale del boton Refresh de un contrato standalone " +
        "en modo edit, donde si es un bug (el boton no refresca nunca). Por eso " +
        "la regla exige ademas la marca del call site, que es lo unico que " +
        "distingue la procedencia y no se mueve con la navegacion.",
    },
    {
      id: "multireport-public-token-expired",
      method: "GET",
      statuses: [400],
      path: /^\/v1\/tenants\/reports\/multireport\/public$/,
      matches: ({ body }) => {
        const message = bodyField(body, "message");
        return (
          typeof message === "string" &&
          /^token (?:has )?expired\.?$/i.test(message.trim())
        );
      },
      probe: {
        // `multireport` y `public` sobreviven a `normalizeUrlForFingerprint`:
        // son palabras sin digitos, no identificadores (verificado).
        requestPath: "/v1/tenants/reports/multireport/public",
        query: { token: "a7f3c1d9b2e8", email: "destinatario@example.com" },
        callSiteMark: "",
        // Cuerpo literal del evento 5d152067207e4e8a93925c6a7fc9d0c1.
        body: { message: "Token has expired." },
      },
      why:
        "MB-APP-EXPRESS-D (1383 eventos desde 2025-12-17, ultimo el " +
        "2026-10-01 en la release mas nueva; Users Impacted: 0 porque lo abre " +
        "un anonimo). Es el enlace publico de multireporte caducado, y el " +
        "front YA lo trata como mensaje de usuario: MultireportView.tsx:175-177 " +
        "pinta `response.data.message` en pantalla. El token sale de un correo, " +
        "asi que caducar es su final normal, no un fallo. " +
        "NO se cubre `{message:'Access denied.'}` del MISMO endpoint, que es la " +
        "otra variante del issue: un token revocado o de otro recurso y un " +
        "fallo de autorizacion del backend tienen esa misma forma, y no hay " +
        "evidencia de que sea un flujo esperado — asi que se sigue capturando. " +
        "Unica regla que mira el TEXTO de un mensaje: es el unico discriminador " +
        "que hay, es una constante inglesa del backend (no texto i18n de " +
        "usuario) y si algun dia se traduce la regla deja de casar, que es el " +
        "lado seguro (vuelve el ruido, no se esconde nada).",
    },
    {
      id: "zip-code-not-found",
      method: "GET",
      statuses: [404],
      path: ZIP_CODE_PATH,
      matches: ({ body }) => {
        const error = bodyField(body, "error");
        return typeof error === "string" && /no encontrado/i.test(error);
      },
      probe: {
        requestPath: ZIP_CODES_NORMALIZATION.sampleRequestPath,
        query: {},
        callSiteMark: "",
        // Cuerpo literal del servicio real (curl a /zip-code/00000).
        body: { error: "Código postal no encontrado" },
      },
      why:
        "MB-APP-EXPRESS-19K. El CP lo teclea el usuario y el hook solo dispara " +
        "con `/^\\d{5}$/`; el front ya trata el 404 como validacion de " +
        "formulario ('El código postal no existe.'). Verificado contra el " +
        "servicio real: el 404 de negocio devuelve " +
        "`{error:'Código postal no encontrado'}`, mientras que un path " +
        "equivocado en el MISMO servicio devuelve HTML (string sin `.error`), " +
        "que por tanto se sigue capturando.",
    },
    {
      id: "investigation-not-found",
      method: "GET",
      statuses: [404],
      path: /^\/v1\/investigations\/:id$/,
      matches: ({ body }) => {
        const detail = bodyField(body, "detail");
        return (
          typeof detail === "string" &&
          /^no investigation matches the given query\.?$/i.test(detail.trim())
        );
      },
      probe: {
        requestPath: "/v1/investigations/310c3a6c-3bf0-4dfb-8966-d20fcac976ad",
        query: {},
        callSiteMark: "",
        // Cuerpo literal del evento 956198f808a149d4a920530f5b8223c4, cuyo
        // `culture.locale` es "es-ES" y aun asi responde en ingles.
        body: { detail: "No Investigation matches the given query." },
      },
      why:
        "MB-APP-EXPRESS-19J. El uuid viene de `useParams()` (link viejo, " +
        "compartido o borrado) y el front ya lleva al usuario a `/not-found`. " +
        "La regex nombra el MODELO: un bug de routing da `{detail:'Not found.'}` " +
        "y un lookup del modelo equivocado da otro nombre, y los dos se siguen " +
        "capturando. Limite declarado: un 404 por fallo de scoping devuelve " +
        "este mismo cuerpo y queda cubierto por el reporte del backend.",
    },
    {
      id: "confirm-pin-wrong-pin",
      method: "POST",
      statuses: [400, 422],
      path: /^\/v2\/reports\/:id\/confirm-pin$/,
      matches: ({ body }) => {
        const code = bodyField(body, "code");
        return (
          (typeof code === "string" || typeof code === "number") &&
          String(code) === "1009"
        );
      },
      probe: {
        // El call site real escribe la url absoluta (`${API}/v2/...`), que
        // normaliza a lo mismo que esta: el origen del API principal se quita y
        // el uuid colapsa a `:id`. Hay un test aparte para la forma absoluta.
        requestPath: "/v2/reports/598b5869-8dea-4697-9120-1eecc3f509c8/confirm-pin",
        query: {},
        callSiteMark: "",
        body: { code: 1009, message: "NIP incorrecto" },
      },
      why:
        "Migracion del bloque hardcodeado de queryErrorReporting.ts. PinConfirm " +
        "gestiona el NIP incorrecto y descuenta intentos, asi que el 1009 es " +
        "input del usuario. El resto del catalogo de CreditReportException NO " +
        "entra aqui: cada codigo necesita su propia evidencia de Sentry.",
    },
  ];

const splitUrl = (url: string): { path: string; inlineQuery: string } => {
  const withoutHash = url.split("#")[0];
  const queryStart = withoutHash.indexOf("?");

  return queryStart === -1
    ? { path: withoutHash, inlineQuery: "" }
    : {
        path: withoutHash.slice(0, queryStart),
        inlineQuery: withoutHash.slice(queryStart + 1),
      };
};

/**
 * Query efectiva: la inline del url mas `config.params`. `params` gana, que es
 * lo que hace axios. Los valores se pasan a string; las reglas solo miran
 * presencia o igualdad literal, nunca interpretan el valor.
 */
const buildQuery = (
  inlineQuery: string,
  params: unknown,
): Record<string, string> => {
  const query: Record<string, string> = {};

  new URLSearchParams(inlineQuery).forEach((value, key) => {
    if (key !== "") {
      query[key] = value;
    }
  });

  if (params instanceof URLSearchParams) {
    params.forEach((value, key) => {
      if (key !== "") {
        query[key] = String(value);
      }
    });
    return query;
  }

  if (params !== null && typeof params === "object") {
    Object.entries(params as Record<string, unknown>).forEach(
      ([key, value]) => {
        // Un parametro sin valor no lo manda axios, asi que para las reglas
        // equivale a no estar.
        if (key === "" || value === undefined || value === null) {
          return;
        }
        query[key] = String(value);
      },
    );
  }

  return query;
};

/**
 * La regla que reconoce este error, o `undefined` si ninguna lo hace.
 *
 * TODO el cuerpo va en try/catch con default `undefined` (= capturar). No es
 * defensivo por gusto: este predicado corre dentro de `beforeSend`, y
 * @sentry/core atrapa un fallo de `beforeSend`, lo registra como error interno
 * y DESCARTA el evento. Un TypeError en una regla no abriria un issue:
 * APAGARIA TODOS, en silencio. Y corre tambien dentro del interceptor de
 * axios, donde una excepcion cambiaria el objeto rechazado y el front dejaria
 * de encontrar `error.response.data.contract_id` (crash cambiado por UI
 * silenciosamente incorrecta, que es peor).
 *
 * El try/catch envuelve el `.find()` COMPLETO y no cada regla: si una regla
 * lanza, no se sigue probando la siguiente — se devuelve "capturar" y punto.
 * Lo unico de la tabla que se evalua FUERA de aqui es su construccion al cargar
 * el modulo (literales, mas el IIFE de `ZIP_CODES_NORMALIZATION`, que tiene su
 * propio try/catch, y el `new RegExp` de `ZIP_CODE_PATH` sobre strings ya
 * escapados); un fallo ahi rompe el ARRANQUE de la app, que es ruidoso, no
 * silencioso.
 *
 * Sale en la primera linea si no es un AxiosError con respuesta: `beforeSend`
 * llama a esto en TODA excepcion de la app, no solo en las de API.
 */
export function matchExpectedBusinessResponse(
  error: unknown,
): ExpectedBusinessResponse | undefined {
  try {
    if (!axios.isAxiosError(error) || !error.response) {
      return undefined;
    }

    const method = error.config?.method?.toUpperCase();
    const rawUrl = error.config?.url;

    if (!method || !rawUrl) {
      return undefined;
    }

    const { status } = error.response;
    const { path: rawPath, inlineQuery } = splitUrl(rawUrl);
    const path = normalizeUrlForFingerprint(rawPath);
    const facts: ExpectedBusinessResponseFacts = {
      body: error.response.data,
      query: buildQuery(inlineQuery, error.config?.params),
      callSiteMark: readExpectedBusinessResponseMark(error.config),
    };

    return EXPECTED_BUSINESS_RESPONSES.find(
      (rule) =>
        rule.method === method &&
        rule.statuses.includes(status) &&
        rule.path.test(path) &&
        rule.matches(facts) === true,
    );
  } catch {
    return undefined;
  }
}

/** Azucar para `beforeSend`, que solo necesita el si/no. */
export function isExpectedBusinessResponse(error: unknown): boolean {
  return matchExpectedBusinessResponse(error) !== undefined;
}
